import { ThemeProvider } from '@emotion/react';
import { datadogRum } from '@datadog/browser-rum';
import getConfig from 'next/config';
import { useEffect } from 'react';
import { ApolloProvider } from '@apollo/client';
import { PublicRuntimeConfigProvider } from '@widget/core/PublicRuntimeConfigProvider';
import { DATADOG_URL_MATCH_REGEXP } from '@widget/config/datadog';

import { writeUtmParamsIntoCookie } from '@lib/utmHandler';
import { unregisterServiceWorkers } from '@lib/utils';
import { GlobalStyles } from '@components/GlobalStyles';
import { defaultTheme } from '@lib/themes';
import { useApollo } from '@lib/apolloClient';

import type { NormalizedCacheObject } from '@apollo/client';
import type { AppProps as NextAppProps } from 'next/app';

const { publicRuntimeConfig } = getConfig();

type PageProps = {
  initialApolloState?: NormalizedCacheObject;
};

type AppProps = Omit<NextAppProps<PageProps>, 'pageProps'> & {
  pageProps: PageProps;
};

const MyApp = ({ Component, pageProps }: AppProps): JSX.Element => {
  const apolloClient = useApollo(pageProps.initialApolloState);

  useEffect(() => {
    if (publicRuntimeConfig.datadog) {
      datadogRum.init({
        applicationId: publicRuntimeConfig.datadog.applicationId,
        clientToken: publicRuntimeConfig.datadog.clientToken,
        site: publicRuntimeConfig.datadog.site,
        service: publicRuntimeConfig.appName,
        version: publicRuntimeConfig.version,
        env: publicRuntimeConfig.appEnv,
        trackUserInteractions: true,
        allowedTracingUrls: [
          {
            match: DATADOG_URL_MATCH_REGEXP,
            propagatorTypes: ['tracecontext'],
          },
        ],
        sessionSampleRate: 10,
        sessionReplaySampleRate: 0,
      });
    }

    if (window.location) {
      writeUtmParamsIntoCookie();
    }

    // @TODO remove this when all the remaining users' service workers'll be unregistred
    unregisterServiceWorkers();
  }, []);

  return (
    <PublicRuntimeConfigProvider publicRuntimeConfig={getConfig().publicRuntimeConfig}>
      <ApolloProvider client={apolloClient}>
        <ThemeProvider theme={defaultTheme}>
          <GlobalStyles />
          <Component {...pageProps} />
        </ThemeProvider>
      </ApolloProvider>
    </PublicRuntimeConfigProvider>
  );
};

export default MyApp;
